import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const QUESTIONS = [
  { key: 'citizenshipStatus', label: 'Citizenship / residency status', options: [['us_citizen', 'U.S. citizen'], ['lpr', 'Lawful permanent resident'], ['other', 'Other']] },
  { key: 'criminalHistory', label: 'Any criminal history that would be disclosed on SBA Form 1919?', yesNo: true },
  { key: 'governmentLoanDefault', label: 'Any prior government loan default or charge-off?', yesNo: true },
  { key: 'bankruptcy', label: 'Any prior or current bankruptcy?', yesNo: true },
  { key: 'pendingLawsuit', label: 'Any pending lawsuit or outstanding judgment?', yesNo: true },
  { key: 'delinquentTaxes', label: 'Any delinquent federal, state, or local taxes?', yesNo: true },
  { key: 'delinquentChildSupport', label: 'Any delinquent child-support obligation?', yesNo: true },
];

export default function EligibilityQuestionnaire({ loanApplicationId }) {
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!loanApplicationId) return;
    getDoc(doc(db, 'loanApplications', loanApplicationId)).then(snapshot => {
      const existing = snapshot.data()?.eligibilityQuestionnaire;
      if (existing) {
        setAnswers(existing.answers || {});
        setNotes(existing.notes || '');
        setSaved(existing.complete === true);
      }
    });
  }, [loanApplicationId]);

  const complete = QUESTIONS.every(question => Boolean(answers[question.key]));
  const save = async () => {
    if (!loanApplicationId || !complete) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'loanApplications', loanApplicationId), {
        eligibilityQuestionnaire: { answers, notes: notes.trim(), complete: true, updatedAt: new Date().toISOString() },
        eligibilityQuestionnaireComplete: true,
        eligibilityQuestionnaireUpdatedAt: serverTimestamp(),
      });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <button type="button" onClick={() => setExpanded(value => !value)} className="flex w-full items-start justify-between gap-4 text-left" aria-expanded={expanded}>
        <div className="flex gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-blue-50"><ClipboardDocumentCheckIcon className="h-6 w-6 text-blue-600" /></div>
          <div><h2 className="text-lg font-bold text-gray-900">Eligibility questionnaire</h2><p className="mt-1 text-sm text-gray-500">Required for an accurate SBA eligibility decision before engagement.</p></div>
        </div>
        <div className="flex items-center gap-2">
          {saved && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700"><CheckCircleIcon className="h-4 w-4" /> Complete</span>}
          {expanded ? <ChevronUpIcon className="h-5 w-5 text-gray-400" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400" />}
        </div>
      </button>
      {expanded && <>
      <div className="mt-5 divide-y divide-gray-100 rounded-xl border border-gray-200">
        {QUESTIONS.map(question => (
          <div key={question.key} className="grid gap-3 p-4 md:grid-cols-[minmax(0,1fr)_260px] md:items-center">
            <label htmlFor={`eligibility-${question.key}`} className="text-sm font-medium leading-5 text-gray-800">{question.label}</label>
            <select id={`eligibility-${question.key}`} value={answers[question.key] || ''} onChange={event => { setAnswers(current => ({ ...current, [question.key]: event.target.value })); setSaved(false); }} className="rounded-lg border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500">
              <option value="">Select an answer</option>
              {(question.yesNo ? [['no', 'No'], ['yes', 'Yes']] : question.options).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
        ))}
      </div>
      <label className="mt-4 block"><span className="text-sm font-medium text-gray-800">Anything we should know? <span className="font-normal text-gray-400">Optional</span></span><textarea value={notes} onChange={event => { setNotes(event.target.value); setSaved(false); }} rows={3} className="mt-2 block w-full rounded-xl border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500" placeholder="Add context for any Yes answer or special circumstance." /></label>
      <div className="mt-4 flex items-center justify-between gap-4"><p className="text-xs text-gray-500">A “Yes” does not automatically mean you are ineligible; it tells us what must be reviewed.</p><button type="button" onClick={save} disabled={!complete || saving} className="rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40">{saving ? 'Saving…' : saved ? 'Saved' : 'Save answers'}</button></div>
      </>}
    </section>
  );
}
